import { usePageMeta } from "@/lib/pageMeta";
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ComposedChart, Line, Legend } from "recharts";
import api, { apiErr } from "@/lib/api";
import { usePortfolio } from "@/context/PortfolioContext";
import { usePrivacy } from "@/context/PrivacyContext";
import { SubPortfolioSelect } from "@/components/SubPortfolioSelect";
import { PortfolioStrip } from "@/components/dashboard/PortfolioStrip";
import { EmptyState } from "@/components/EmptyState";
import { DividendCoverage } from "@/components/dividends/DividendCoverage";
import { AnnualIncomeReport } from "@/components/dividends/AnnualIncomeReport";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtCurrency, fmtCompact, fmtPct, fmtNumber, fmtDate } from "@/lib/format";
import { CHART_COLORS } from "@/lib/constants";
import { Coins, TrendingUp, CalendarClock, CalendarDays, RefreshCw, Trash2, ChevronRight, Percent } from "lucide-react";
import { toast } from "sonner";

function TaxRatesCard() {
    const qc = useQueryClient();
    const { data } = useQuery({ queryKey: ["div-tax-rates"], queryFn: async () => (await api.get("/dividends/tax-rates")).data });
    const [edits, setEdits] = useState({});
    const items = data?.items || [];
    const defPct = ((data?.default_rate ?? 0.15) * 100).toFixed(0);

    const save = async (ticker, raw) => {
        const pct = String(raw).trim();
        const rate = pct === "" ? null : Math.max(0, Math.min(100, parseFloat(pct))) / 100;
        if (pct !== "" && !Number.isFinite(rate)) return;
        try {
            const { data: res } = await api.put("/dividends/tax-rates", { ticker, rate });
            toast.success(`${ticker}: ${(res.rate * 100).toFixed(res.rate * 100 % 1 ? 2 : 0)}% withholding${res.recomputed ? ` · ${res.recomputed} updated` : ""}`);
            setEdits((e) => { const n = { ...e }; delete n[ticker]; return n; });
            qc.invalidateQueries();
        } catch (e) { toast.error(apiErr(e)); }
    };

    if (!items.length) return null;
    return (
        <Card className="p-4" data-testid="div-tax-rates-card">
            <div className="flex items-center gap-2 mb-1"><Percent className="w-5 h-5 text-primary" /><h2 className="font-heading text-lg font-semibold">Withholding Tax Rates</h2></div>
            <p className="text-sm text-muted-foreground mb-4">Auto-recorded dividends apply <strong>{defPct}%</strong> tax by default. Override a stock's rate below and its dividends recompute instantly. Dividends you hand-edit are never changed.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {items.map((it) => {
                    const shown = edits[it.ticker] !== undefined ? edits[it.ticker] : (it.rate * 100).toFixed(it.rate * 100 % 1 ? 2 : 0);
                    return (
                        <div key={it.ticker} className={`flex items-center justify-between gap-2 border rounded-lg px-3 py-2 ${it.custom ? "border-primary/40 bg-primary/5" : "border-border"}`} data-testid={`div-taxrate-${it.ticker}`}>
                            <span className="num font-medium text-sm">{it.ticker}</span>
                            <div className="flex items-center gap-1">
                                <Input type="number" step="0.5" min="0" max="100" value={shown}
                                    onChange={(e) => setEdits((s) => ({ ...s, [it.ticker]: e.target.value }))}
                                    onBlur={(e) => { if (edits[it.ticker] !== undefined) save(it.ticker, e.target.value); }}
                                    onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                                    className="w-16 h-8 text-right num" data-testid={`div-taxrate-input-${it.ticker}`} />
                                <span className="text-muted-foreground text-sm">%</span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </Card>
    );
}

const fmtMonth = (ym) => {
    const [y, m] = ym.split("-");
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-US", { month: "short", year: "2-digit" });
};

const fmtMonthLong = (ym) => {
    const [y, m] = ym.split("-");
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

const fmtDayMon = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });

// Continuous month-by-month series (gaps filled with 0) + trailing-12-month rolling income.
const buildTrend = (byMonth) => {
    if (!byMonth || !byMonth.length) return [];
    const map = Object.fromEntries(byMonth.map((m) => [m.month, m.amount]));
    const [sy, sm] = byMonth[0].month.split("-").map(Number);
    const [ly, lm] = byMonth[byMonth.length - 1].month.split("-").map(Number);
    const rows = [];
    let y = sy, mo = sm;
    while (y < ly || (y === ly && mo <= lm)) {
        const key = `${y}-${String(mo).padStart(2, "0")}`;
        rows.push({ month: key, amount: map[key] || 0 });
        mo++; if (mo > 12) { mo = 1; y++; }
    }
    return rows.map((row, i) => ({
        ...row,
        label: fmtMonth(row.month),
        ttm: rows.slice(Math.max(0, i - 11), i + 1).reduce((a, r) => a + r.amount, 0),
    }));
};

// net dividends over the 12 most recent calendar months (including the current one)
function last12(byMonth) {
    const d = new Date();
    d.setMonth(d.getMonth() - 11);
    const from = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    return (byMonth || []).filter((m) => m.month >= from).reduce((t, m) => t + m.amount, 0);
}

export default function Dividends() {
    const { selectedSub, setSelectedSub } = usePortfolio();
    const { mask } = usePrivacy();
    const qc = useQueryClient();
    const [syncing, setSyncing] = useState(false);
    const [orphanOpen, setOrphanOpen] = useState(false);
    const [orphanLoading, setOrphanLoading] = useState(false);
    const [orphans, setOrphans] = useState(null);
    const [cleaning, setCleaning] = useState(false);
    const autoRan = useRef(false);
    const { data, isLoading } = useQuery({ queryKey: ["dividends", selectedSub], queryFn: async () => (await api.get(`/portfolio/dividends?sub=${selectedSub}`)).data });
    const forecast = useQuery({ queryKey: ["div-forecast", selectedSub], queryFn: async () => (await api.get(`/portfolio/dividends/forecast?sub=${selectedSub}`)).data });
    const calendar = useQuery({ queryKey: ["div-calendar", selectedSub], queryFn: async () => (await api.get(`/portfolio/dividends/calendar?sub=${selectedSub}`)).data });

    const runSync = async (silent = false) => {
        setSyncing(true);
        try {
            const { data: res } = await api.post("/dividends/sync");
            if (res.added > 0) {
                qc.invalidateQueries();
                toast.success(`Auto-recorded ${res.added} dividend${res.added === 1 ? "" : "s"} from market data`);
            } else if (!silent) {
                toast.info("Dividends are already up to date");
            }
        } catch (e) {
            if (!silent) toast.error(apiErr(e));
        } finally {
            setSyncing(false);
        }
    };

    useEffect(() => {
        if (autoRan.current) return;
        autoRan.current = true;
        runSync(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- run exactly once on mount (guarded by autoRan)
    }, []);

    const loadOrphans = async () => {
        setOrphanOpen(true); setOrphanLoading(true); setOrphans(null);
        try { const { data: res } = await api.get("/dividends/orphans/preview"); setOrphans(res.orphans); }
        catch (e) { toast.error(apiErr(e)); setOrphanOpen(false); } finally { setOrphanLoading(false); }
    };

    const applyCleanup = async () => {
        setCleaning(true);
        try {
            const ids = orphans.map((o) => o.id);
            const { data: res } = await api.post("/dividends/orphans/cleanup", { ids });
            toast.success(`Removed ${res.deleted} incorrect dividend${res.deleted === 1 ? "" : "s"}`);
            setOrphanOpen(false); qc.invalidateQueries();
        } catch (e) { toast.error(apiErr(e)); } finally { setCleaning(false); }
    };

    const trendData = buildTrend(data?.by_month);
    const f = forecast.data;
    const projData = (f?.projected_months || []).map((m) => ({ month: fmtMonth(m.month), amount: m.amount }));
    const hasForecast = f && f.forward_annual_income > 0;
    const hasHistory = (data?.count ?? 0) > 0;

    const cal = calendar.data;
    const calEvents = cal?.events || [];
    const toDate = (s) => new Date(s + "T00:00:00");
    const exDates = calEvents.map((e) => toDate(e.ex_date));
    const payDates = calEvents.map((e) => toDate(e.pay_date));
    const firstMonth = calEvents.length ? toDate(calEvents[0].ex_date) : new Date();
    const eventsByMonth = {};
    calEvents.forEach((e) => { const k = e.ex_date.slice(0, 7); (eventsByMonth[k] ||= []).push(e); });

    const tooltipStyle = { background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 10, fontSize: 12 };

    usePageMeta("Income tracking & forward estimates");
    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2 ml-auto">
                    <Button variant="outline" onClick={loadOrphans} data-testid="cleanup-dividends-btn">
                        <Trash2 className="w-4 h-4 mr-1.5" />Clean up
                    </Button>
                    <Button variant="outline" onClick={() => runSync(false)} disabled={syncing} data-testid="sync-dividends-btn">
                        <RefreshCw className={`w-4 h-4 mr-1.5 ${syncing ? "animate-spin" : ""}`} />Sync from market
                    </Button>
                    <SubPortfolioSelect value={selectedSub} onChange={setSelectedSub} />
                </div>
            </div>

            <Dialog open={orphanOpen} onOpenChange={setOrphanOpen}>
                <DialogContent className="max-w-lg" data-testid="cleanup-dividends-dialog">
                    <DialogHeader><DialogTitle className="font-heading">Remove incorrect auto-dividends</DialogTitle></DialogHeader>
                    {orphanLoading ? (
                        <div className="py-8 text-center text-sm text-muted-foreground">Scanning dividends…</div>
                    ) : orphans && orphans.length === 0 ? (
                        <div className="py-8 text-center text-sm text-muted-foreground" data-testid="cleanup-empty">No stray dividends found — every auto-recorded dividend falls within a period you held the stock.</div>
                    ) : orphans ? (
                        <>
                            <p className="text-sm text-muted-foreground">These {orphans.length} auto-recorded dividend{orphans.length === 1 ? "" : "s"} are dated <strong>before your first purchase</strong> of the stock, so you did not actually own it then. They will be deleted.</p>
                            <div className="max-h-72 overflow-y-auto text-xs space-y-1" data-testid="cleanup-list">
                                {orphans.map((o) => (
                                    <div key={o.id} className="flex items-center justify-between gap-2 border-b border-border/40 py-1">
                                        <span className="num font-medium">{o.ticker} · {o.sub_portfolio}</span>
                                        <span className="flex items-center gap-2">
                                            <span className="text-loss">{fmtDate(o.date)}</span>
                                            <span className="text-muted-foreground">{o.first_buy ? `first buy ${fmtDate(o.first_buy)}` : "never purchased"}</span>
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : null}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setOrphanOpen(false)}>Cancel</Button>
                        {orphans && orphans.length > 0 && (
                            <Button onClick={applyCleanup} disabled={cleaning} className="bg-loss text-white hover:bg-loss/90" data-testid="cleanup-apply-btn">
                                {cleaning ? "Removing…" : `Delete ${orphans.length}`}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {(hasHistory || hasForecast) && (
                <PortfolioStrip cols={3} cells={[
                    { testId: "div-year", label: "Received This Year (net)", value: mask(fmtCurrency(data?.this_year || 0)), sub: `this month ${mask(fmtCurrency(data?.this_month || 0))}` },
                    { testId: "div-ttm", label: "Last 12 Months (net)", value: mask(fmtCurrency(last12(data?.by_month))), sub: data?.total ? `all-time ${mask(fmtCurrency(data.total))} net` : undefined },
                    { testId: "div-forward-income", label: "Expected Next 12 Months", value: hasForecast ? mask(fmtCurrency(f.forward_annual_income)) : "—",
                      sub: hasForecast ? `${fmtPct(f.portfolio_forward_yield, { signed: false })} forward yield · ${mask(fmtCurrency(f.monthly_avg_income))}/mo` : undefined },
                ]} />
            )}

            {/* Forward estimates */}
            {forecast.isLoading ? <Skeleton className="h-24 rounded-xl" /> : hasForecast && (
                <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground uppercase tracking-wider"><TrendingUp className="w-4 h-4" />Forward Estimates</div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                        <Card className="lg:col-span-2 p-4">
                            <div className="flex items-center gap-2 mb-4"><CalendarClock className="w-5 h-5 text-primary" /><h2 className="font-heading text-lg font-semibold">Projected Income — Next 12 Months</h2></div>
                            {projData.length === 0 ? <p className="text-sm text-muted-foreground">No upcoming dividends projected for current holdings.</p> : (
                                <ResponsiveContainer width="100%" height={260}>
                                    <BarChart data={projData} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.4} vertical={false} />
                                        <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                                        <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} width={52} tickFormatter={fmtCompact} />
                                        <Tooltip cursor={{ fill: "hsl(var(--muted) / 0.4)" }} contentStyle={tooltipStyle} formatter={(v) => fmtCurrency(v)} />
                                        <Bar dataKey="amount" radius={[4, 4, 0, 0]} fill="hsl(var(--accent))" />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </Card>
                        <Card className="p-4">
                            <h2 className="font-heading text-lg font-semibold mb-4">Projected by Security</h2>
                            <div className="space-y-2 max-h-72 overflow-y-auto">
                                {f.by_security.map((d, i) => (
                                    <div key={d.ticker} className="flex items-center justify-between text-sm py-1.5" data-testid={`div-forecast-${d.ticker}`}>
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                                            <div className="min-w-0">
                                                <div className="font-medium num">{d.ticker}</div>
                                                <div className="text-[10px] text-muted-foreground num">{fmtNumber(d.shares, 0)} sh · {d.forward_yield != null ? `${d.forward_yield}% yld` : "—"}</div>
                                            </div>
                                        </div>
                                        <span className="num font-medium">{mask(fmtCurrency(d.annual_income))}/yr</span>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </div>
                    <p className="text-xs text-muted-foreground">{f.disclaimer}</p>
                </div>
            )}

            {/* Dividend calendar */}
            {calendar.isLoading ? <Skeleton className="h-72 rounded-xl" /> : calEvents.length > 0 && (
                <div className="space-y-3" data-testid="dividend-calendar-section">
                    <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground uppercase tracking-wider"><CalendarDays className="w-4 h-4" />Upcoming Dividend Calendar</div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        <Card className="p-4">
                            <div className="flex items-center gap-4 mb-3 text-xs">
                                <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border-b-2 border-primary" />Ex-dividend</span>
                                <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-[4px] bg-accent" />Pay date</span>
                            </div>
                            <Calendar
                                mode="single" numberOfMonths={1} defaultMonth={firstMonth} showOutsideDays
                                modifiers={{ ex: exDates, pay: payDates }}
                                modifiersStyles={{
                                    ex: { fontWeight: 700, textDecoration: "underline", textDecorationColor: "hsl(var(--primary))", textUnderlineOffset: "3px" },
                                    pay: { backgroundColor: "hsl(var(--accent))", color: "#fff", borderRadius: "8px" },
                                }}
                                className="p-0"
                                data-testid="dividend-calendar"
                            />
                        </Card>
                        <Card className="p-4">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="font-heading text-lg font-semibold">Upcoming Payments</h2>
                                <span className="text-xs text-muted-foreground num">{mask(fmtCurrency(cal.total_est))} est. total</span>
                            </div>
                            <div className="space-y-4 max-h-[360px] overflow-y-auto pr-1">
                                {Object.entries(eventsByMonth).map(([month, evs]) => (
                                    <div key={month}>
                                        <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">{fmtMonthLong(month)}</div>
                                        <div className="space-y-1.5">
                                            {evs.map((e, i) => (
                                                <div key={`${e.ticker}-${e.ex_date}-${i}`} className="flex items-center justify-between p-2 rounded-lg border border-border" data-testid={`div-event-${e.ticker}-${e.ex_date}`}>
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-semibold num text-sm">{e.ticker}</span>
                                                            <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${e.confirmed ? "border-gain/40 text-gain" : "border-border text-muted-foreground"}`}>{e.confirmed ? "Confirmed" : "Est."}</Badge>
                                                        </div>
                                                        <div className="text-[10px] text-muted-foreground num">Ex {fmtDayMon(e.ex_date)} · Pay {fmtDayMon(e.pay_date)}</div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="num font-medium text-sm">{mask(fmtCurrency(e.est_amount))}</div>
                                                        <div className="text-[10px] text-muted-foreground num">{fmtCurrency(e.amount_per_share, { decimals: 2 })}/sh</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-3">Estimated dates project the most recent payment cadence. Confirmed dates come from the data provider's calendar.</p>
                        </Card>
                    </div>
                </div>
            )}

            <DividendCoverage sub={selectedSub} />

            <AnnualIncomeReport sub={selectedSub} />

            {/* Historical dividends */}
            {isLoading ? <Skeleton className="h-24 rounded-xl" /> : !hasHistory ? (
                <Card><EmptyState icon={Coins} title="No dividends recorded yet" description="Add DIVIDEND transactions to track received income here. Forward estimates above are based on your current holdings." testId="dividends-empty" /></Card>
            ) : (
                <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground uppercase tracking-wider pt-2"><Coins className="w-4 h-4" />Received Income</div>

                    <TaxRatesCard />

                    <Card className="p-4">
                        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                            <h2 className="font-heading text-lg font-semibold">Monthly Dividend Income (received)</h2>
                            <div className="flex items-center gap-4 text-[11px] text-muted-foreground">
                                <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: "hsl(var(--primary))" }} />Monthly</span>
                                <span className="inline-flex items-center gap-1.5"><span className="w-4 h-0.5 rounded-full" style={{ background: "hsl(var(--accent))" }} />Trailing 12-mo</span>
                            </div>
                        </div>
                        {trendData.length === 0 ? <p className="text-sm text-muted-foreground">No data.</p> : (
                            <ResponsiveContainer width="100%" height={280}>
                                <ComposedChart data={trendData} margin={{ top: 10, right: 8, left: 0, bottom: 0 }} data-testid="dividend-trend-chart">
                                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.4} vertical={false} />
                                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} minTickGap={24} />
                                    <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} width={52} tickFormatter={fmtCompact} />
                                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "hsl(var(--accent))" }} tickLine={false} axisLine={false} width={52} tickFormatter={fmtCompact} />
                                    <Tooltip cursor={{ fill: "hsl(var(--muted) / 0.4)" }} contentStyle={tooltipStyle} formatter={(v, n) => [fmtCurrency(v), n]} />
                                    <Bar yAxisId="left" dataKey="amount" name="Monthly" radius={[4, 4, 0, 0]} fill="hsl(var(--primary))" />
                                    <Line yAxisId="right" type="monotone" dataKey="ttm" name="Trailing 12-mo" stroke="hsl(var(--accent))" strokeWidth={2.5} dot={false} />
                                </ComposedChart>
                            </ResponsiveContainer>
                        )}
                        <p className="text-[11px] text-muted-foreground mt-2">Bars show income received each month (left axis). The line is the trailing-12-month total (right axis) — a smoothed view of your passive-income trend.</p>
                    </Card>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        <Card className="p-4">
                            <h2 className="font-heading text-lg font-semibold mb-4">By Security</h2>
                            <div className="space-y-2">
                                {data.by_security.map((d, i) => (
                                    <div key={d.ticker} className="flex items-center justify-between text-sm py-1.5" data-testid={`div-security-${d.ticker}`}>
                                        <div className="flex items-center gap-2">
                                            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                                            <span className="font-medium num">{d.ticker}</span>
                                        </div>
                                        <span className="num font-medium">{mask(fmtCurrency(d.amount))}</span>
                                    </div>
                                ))}
                            </div>
                        </Card>
                        <Card className="p-4">
                            <h2 className="font-heading text-lg font-semibold mb-4">By Year</h2>
                            <div className="space-y-2">
                                {data.by_year.map((d) => (
                                    <div key={d.year} className="flex items-center justify-between text-sm py-1.5">
                                        <span className="num text-muted-foreground">{d.year}</span>
                                        <span className="num font-medium">{mask(fmtCurrency(d.amount))}</span>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </div>
                </div>
            )}
        </div>
    );
}
